/**
 * Renders the metric chips at the top of the analysis page. Mirrors
 * the `.metric-chip` markup and the `renderMetrics()` formatting from
 * the legacy `templates/analysis.html` + `static/analysis.js`. Nullable
 * fields (avg L/100km, avg €/100km) collapse to "—".
 */

import type { AnalysisMetrics } from "../../api/types";

export interface MetricChipsProps {
  metrics: AnalysisMetrics;
  isLongPeriod: boolean;
}

const EM_DASH = "\u2014";

function formatLiters(value: number): string {
  return `${value.toFixed(2)} L`;
}

function formatEur(value: number): string {
  return `${value.toFixed(2)} €`;
}

function formatPricePerLiter(value: number): string {
  return `${value.toFixed(3)} €/L`;
}

function formatKm(value: number | null | undefined): string {
  return value === null || value === undefined ? EM_DASH : `${value.toFixed(1)} km`;
}

function formatLPer100(value: number | null): string {
  return value === null ? EM_DASH : `${value.toFixed(2)} L/100km`;
}

function formatEurPer100(value: number | null): string {
  return value === null ? EM_DASH : `${value.toFixed(2)} €/100km`;
}

interface Chip {
  label: string;
  value: string;
  metric: string;
}

export default function MetricChips({ metrics, isLongPeriod }: MetricChipsProps) {
  const chips: Chip[] = [
    {
      label: "Km driven",
      value: formatKm(metrics.total_km_driven),
      metric: "total_km_driven",
    },
    {
      label: "Fuel",
      value: formatLiters(metrics.total_liters),
      metric: "total_liters",
    },
    {
      label: "Spent",
      value: formatEur(metrics.total_eur),
      metric: "total_eur",
    },
    ...(isLongPeriod
      ? [
          {
            label: "Avg €/month",
            value: formatEur(metrics.avg_eur_per_month),
            metric: "avg_eur_per_month",
          },
        ]
      : []),
    {
      label: "Avg €/L",
      value: formatPricePerLiter(metrics.avg_price_per_liter),
      metric: "avg_price_per_liter",
    },
    {
      label: "Avg L/100km",
      value: formatLPer100(metrics.avg_l_per_100km),
      metric: "avg_l_per_100km",
    },
    {
      label: "Avg €/100km",
      value: formatEurPer100(metrics.avg_cost_per_100km),
      metric: "avg_cost_per_100km",
    },
  ];

  return (
    <div className="metric-row" data-metric-row>
      {chips.map((chip) => (
        <article key={chip.metric} className="metric-chip">
          <span className="metric-chip__label">{chip.label}</span>
          <span className="metric-chip__value" data-metric={chip.metric}>
            {chip.value}
          </span>
        </article>
      ))}
    </div>
  );
}
